import { Typography } from '@client/new/components/basic';
import {
  CustomDomainStatus,
  useCustomDomainsQuery,
} from '../../../../../../../generated/graphql';
import { useUser } from '@client/new/hooks/use-user';
import { DnsConfigInstructions } from './DnsConfigInstructions';
import DomainsStepper from './DomainsSetupStepper';
import { useDomainSetup } from './useDomainSetup';
import { DomainForm } from './DomainSetupForm';
import { CustomDomainEntity } from './domainForm.schema';
import { goBack } from '@client/routes';
import { ResetDomainButton } from './ResetDomainButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import toast from 'react-hot-toast';
import ContentCopy from '@mui/icons-material/ContentCopy';

const withData = (Component: typeof DomainSetup) => (props: any) => {
  const user = useUser();
  const creatorId = Number(props.params.influencerId || user?.influencer?.id);
  const { data, loading, error, refetch } = useCustomDomainsQuery({
    variables: {
      id: creatorId,
    },
    fetchPolicy: 'no-cache',
  });

  if (error) {
    return <div>{error.message}</div>;
  }

  if (loading || !data?.creator) {
    return null;
  }

  return (
    <Component
      {...props}
      creatorId={creatorId}
      customDomains={data?.creator.customDomains}
      refetch={() => refetch()}
    />
  );
};

type DomainSetupProps = {
  customDomains: any[];
  creatorId: number;
  refetch: () => void;
};

function DomainSetup({ customDomains, creatorId, refetch }: DomainSetupProps) {
  const domainSetup = useDomainSetup({
    creatorId,
    customDomains,
    refetch,
  });

  const unrecoverableError = Boolean(
    domainSetup.domainEntity?.additionalInfo?.error,
  );

  if (!creatorId) {
    throw new Error('CreatorId is not defined');
  }

  return (
    <div>
      <nav className="mb-8">
        <a
          className="text-black cursor-pointer font-bold"
          onClick={() => goBack()}
        >
          &lt;&nbsp;Back
        </a>
      </nav>
      <main className="flex flex-col gap-6 bg-white p-10 rounded-[20px]">
        <DomainsStepper activeStep={domainSetup.activeStep}>
          <section>
            <DomainForm
              isLoading={domainSetup.loading.addDomainLoading}
              domain={domainSetup.domain}
              subdomain={domainSetup.subdomain}
              appUrl={domainSetup.appUrl}
              register={domainSetup.register}
              onSubmit={domainSetup.onSubmit}
              control={domainSetup.control}
              errorMessage={domainSetup.errorMessage}
            />
          </section>

          <section>
            <DnsConfigInstructions
              domain={domainSetup.domain}
              dnsRecords={
                domainSetup.domainEntity?.additionalInfo?.dnsRecords || []
              }
              onSubmit={domainSetup.onConfirmDnsSetup}
              isLoading={domainSetup.loading.confirmDomainLoading}
              control={domainSetup.control}
              removeDomainLoading={domainSetup.loading.removeDomainLoading}
              onRemoveDomain={domainSetup.onRemoveDomain}
              hideConfirmDomainModal={domainSetup.hideConfirmDomainModal}
              showConfirmDomainModal={domainSetup.showConfirmDomainModal}
              errorMessage={domainSetup.errorMessage}
              hostingProvider={domainSetup.hostingProvider}
              showCaaTable={domainSetup.showCaaTable}
              configError={domainSetup.configError}
            />
          </section>

          <section>
            {unrecoverableError ? (
              <UnrecoverableError
                creatorId={creatorId}
                domainId={domainSetup.domainEntity.id}
              />
            ) : (
              <InfoAfterRegistration domainEntity={domainSetup.domainEntity} />
            )}
          </section>
        </DomainsStepper>
      </main>
    </div>
  );
}

export default withData(DomainSetup);

function UnrecoverableError({
  creatorId,
  domainId,
}: {
  creatorId: number;
  domainId: number;
}) {
  return (
    <div className="flex flex-col gap-6 bg-white text-center min-h-[55vh] justify-center">
      <Typography variant="h2" className="font-bold">
        {' '}
        Something went wrong 😕{' '}
      </Typography>
      <Typography variant="body1" className="font-medium">
        There’s an issue on our side.
      </Typography>
      <Typography variant="body1" className="max-w-[370px] mx-auto">
        Please contact our{' '}
        <a
          href="mailto:creatorsupport@playbookapp.io"
          className="text-black underline"
        >
          support team
        </a>{' '}
        and we will resolve this issue quickly.
      </Typography>
      <ResetDomainButton
        influencerId={creatorId}
        label="Reset your domain and start over"
        domainId={domainId}
        color="#5F1ECC"
      />
    </div>
  );
}

function InfoAfterRegistration({
  domainEntity,
}: {
  domainEntity: CustomDomainEntity;
}) {
  const handleCopy = () => {
    navigator.clipboard.writeText(domainEntity.domain);
    toast.success('Copied to clipboard');
  };
  if (domainEntity.status === CustomDomainStatus.InfraRunning) {
    return (
      <section className="flex flex-col gap-8 justify-center items-center min-h-[55vh]">
        <Typography variant="h2">Your domain is up and running 🎉</Typography>
        <Typography className="font-medium">
          Copy domain and start using it
        </Typography>
        <div className="px-4 py-3 flex gap-4 items-center bg-[#F8F8F8] rounded-[8px]">
          <FontAwesomeIcon
            icon={faCircleCheck}
            className="text-[#0ACC2F] text-[20px]"
          />
          <span className="md:min-w-[400px] font-medium">
            https://{domainEntity.domain}
          </span>
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={handleCopy}
          >
            <ContentCopy color="primary" className="text-[18px]"></ContentCopy>
            <span>Copy</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-8 justify-center items-center min-h-[55vh]">
      <Typography variant="h2">You’re done! 🙌</Typography>
      <div className="text-center">
        <Typography variant="body1" className="font-medium">
          It might take up to 36 hours to get your domain up & running.
        </Typography>
        <Typography variant="body1" className="font-medium">
          Please check this page later to confirm domain status.
        </Typography>
      </div>
      <div className="text-center">
        <Typography variant="body1" className="font-medium">
          For now you can use your subdomain
        </Typography>
        <a
          href={`https://${domainEntity.domain}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#5F1ECC] font-medium"
        >
          {domainEntity.domain}
        </a>
      </div>
    </section>
  );
}
